import { Fragment } from 'react';

import {
  Badge,
  Button,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from '@chakra-ui/react';

import { CellContext, ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { format, parseISO } from 'date-fns';

import {
  Company,
  CompanyStatus,
  DEFAULT_DATE_FORMAT,
  MoreCircleOutline,
} from '@aslanpay/portal-common-lib';

import { CompanyActions, CompanyRow } from 'app/components/UI/Table/Table.types';

import CompanyStatusValues from 'app/constants/companies';
import formatNumber from 'app/utils/formatting';
import { CompanyActionsIds, companyActionsValues, CompanyField } from 'app/constants/table';

function getCompanyColumns(actions: CompanyActions) {
  const columnHelper = createColumnHelper<CompanyRow>();

  const fields = Object.keys(CompanyField) as Array<keyof CompanyRow>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<CompanyRow, any>[] = fields.map((field) => {
    return columnHelper.accessor(field as keyof CompanyRow, {
      cell: ({ getValue }) => {
        const value = getValue() as string;

        switch (field as keyof typeof CompanyField) {
          case 'name':
            return (
              <Text as="h6" textStyle="h6SemiBold">
                {value}
              </Text>
            );
          case 'displayName':
            return value || '-';
          case 'createdAt':
            return format(parseISO(value), DEFAULT_DATE_FORMAT);
          case 'status':
            return (
              <Badge colorScheme={`badge.${CompanyStatusValues[value as CompanyStatus]?.color}`}>
                {CompanyStatusValues[value as CompanyStatus]?.value}
              </Badge>
            );
          case 'payCalendars':
          case 'employees':
            return formatNumber(value as unknown as number);
          default:
            return value;
        }
      },
      header: CompanyField[field as keyof typeof CompanyField],
      enableSorting:
        field !== 'payCalendars' && field !== 'employees' && field !== 'activeServices',
      meta: {
        getCellContext: (context: CellContext<CompanyRow, unknown>) => {
          return {
            onClick: () => actions.select(context.row.original as Company),
          };
        },
      },
    });
  });

  columns.push(
    columnHelper.display({
      id: 'select',
      cell: ({ row }) => (
        <Button variant="link" size="sm" onClick={() => actions.select(row.original as Company)}>
          Select
        </Button>
      ),
    })
  );

  columns.push(
    columnHelper.display({
      id: 'options',
      meta: {
        getCellContext: (context: CellContext<CompanyRow, unknown>) => {
          if (context.column.id === 'options') {
            return {
              style: { padding: 0, textAlign: 'end' },
            };
          }
          return undefined;
        },
        customStyles: { justifyContent: 'center' },
        pin: 'right',
      },
      cell: (info) => (
        <Menu autoSelect={false} strategy="fixed">
          <MenuButton
            as={IconButton}
            aria-label="Options"
            icon={<MoreCircleOutline boxSize="20px" />}
            variant="menu"
          />
          <Portal>
            <MenuList>
              {Object.keys(actions).flatMap((action, index) => {
                const actionValue = companyActionsValues.find((value) => value.id === action);

                // NOTE: Filtering the delete option out because for now we won't allow that.
                if (!actionValue || actionValue.id === CompanyActionsIds.DELETE) return [];

                return (
                  <Fragment key={action}>
                    {index > 0 && <MenuDivider />}
                    <MenuItem
                      key={action}
                      color={action === CompanyActionsIds.DELETE ? 'system.error' : 'brand.black'}
                      onClick={() =>
                        actions[action as keyof typeof actions](info.row.original as Company)
                      }
                      icon={
                        <Icon
                          as={actionValue?.icon}
                          boxSize="14px"
                          color={
                            action === CompanyActionsIds.DELETE ? 'system.error' : 'brand.darkGray'
                          }
                        />
                      }
                    >
                      {actionValue?.value}
                    </MenuItem>
                  </Fragment>
                );
              })}
            </MenuList>
          </Portal>
        </Menu>
      ),
    })
  );

  return columns;
}

export default getCompanyColumns;
